import { defineStore } from 'pinia';
import { mande } from 'mande';

import { auth0 } from '../auth0.js';
import { fetchWithTimeout, isTransportError } from '@/utils/networkUtils';
import { oneMonthAgo } from '@/utils/registers';

const READING_SUBMIT_TIMEOUT_MS = 15_000;
const PHOTO_UPLOAD_URL_TIMEOUT_MS = 10_000;

export const initApi = async () => {
  const token = await auth0.getAccessTokenSilently();

  return mande(`${import.meta.env.VITE_APP_API_URL}/readings`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

export const useStore = defineStore('reading', {
  state: (): { loading: boolean; readings: ETNReading[] } => {
    return {
      readings: [],
      loading: false
    };
  },
  actions: {
    async listReadings(accountId: string) {
      try {
        this.loading = true;

        const api = await initApi();
        const response = (await api.get({
          query: {
            accountId,
            $limit: 10,
            $sort: 'submittedAt:-1',
            $select: 'value,submittedAt,type,isReset,registerId'
          }
        })) as ETNAPIResponse<ETNReading[]>;

        const readings = response.data.map((reading) => {
          const [year, month, day] = reading.submittedAt.slice(0, 10).split('-');
          const [hour, min, sec] = reading.submittedAt.slice(11, 19).split(':');
          reading.submittedAt = `${day}/${month}/${year} ${hour}:${min}:${sec}`;
          return reading;
        });

        this.readings = readings;

        this.loading = false;
        return this.readings;
      } catch (e) {
        this.loading = false;
        return e;
      }
    },
    async submitReading(
      reading: Omit<ETNReading, '_id' | 'companyId' | 'entityId'>
    ): Promise<string | null> {
      const api = await initApi();
      // The submit endpoint returns the created reading document directly
      // (dbResponseWrapper passes the item through unwrapped), so we capture
      // its _id to let the queue fetch the photo from S3 on demand later
      // instead of holding the base64 in memory.
      const created = (await fetchWithTimeout(
        (signal) => api.post('submit', reading, { signal }),
        READING_SUBMIT_TIMEOUT_MS
      )) as ETNReading | undefined;

      return created?._id ?? null;
    },
    async getPhotoUrl(readingId: string): Promise<string | null> {
      try {
        const api = await initApi();
        const response = (await api.get(`${readingId}/photo`)) as { signedUrl?: string };
        return response?.signedUrl ?? null;
      } catch (e) {
        // A failed photo fetch is non-fatal — the detail view simply shows no
        // image. Log for visibility rather than surfacing to the user.
        console.error('Failed to load reading photo', e);
        return null;
      }
    },
    async recentRegisterIds(accountId: string): Promise<string[]> {
      try {
        const api = await initApi();
        const response = (await api.get({
          query: {
            accountId,
            $limit: 20,
            $sort: 'submittedAt:-1',
            $select: 'registerId,submittedAt'
          }
        })) as ETNAPIResponse<Pick<ETNReading, 'registerId' | 'submittedAt'>[]>;

        const cutoff = oneMonthAgo();

        return response.data
          .filter((reading) => new Date(reading.submittedAt) >= cutoff)
          .map((reading) => reading.registerId || '1');
      } catch (e) {
        // A failed fetch must never break the confirmation banner — the offline
        // queue still covers the just-submitted reading, so we degrade to an empty
        // server set. Log for visibility (matching listAccounts / listAssets) rather
        // than surfacing to the user, since this is a best-effort backfill.
        console.error('Failed to load recent readings', e);
        return [];
      }
    },
    async getPhotoUploadUrl(format: string): Promise<{ uploadUrl: string; s3Key: string } | null> {
      try {
        const api = await initApi();
        const response = await fetchWithTimeout(
          (signal) =>
            api.get('/photo-upload-url', {
              query: { contentType: `image/${format}` },
              signal
            }),
          PHOTO_UPLOAD_URL_TIMEOUT_MS
        );

        return response as { uploadUrl: string; s3Key: string };
      } catch (e) {
        // Re-throw transport errors so processQueueItem can mark the item pending
        // for auto-retry; non-transport errors (e.g. 401 from an expired token)
        // keep the existing `return null` so uploadPhoto can decide how to proceed.
        if (isTransportError(e)) throw e;
        return null;
      }
    }
  }
});
